/**
 * Whether live updates are actually connected.
 *
 * Realtime is opt-in and off by default (`ABLY_ENABLED=false`), and every
 * surface that uses it -- notifications, project DMs, comments -- degraded in
 * silence: messages still arrive, but only when something re-fetches. Nothing
 * told the reader which mode they were in, so "my messages are slow" and "live
 * updates are switched off" looked identical.
 */

import { useTranslation } from "react-i18next";
import { useNotificationsStore } from "@/features/settings/store/notifications.store";
import { cn } from "@/lib/utils";

export function RealtimeStatus({ className }: { className?: string }) {
	const { t } = useTranslation();
	const status = useNotificationsStore((state) => state.realtimeStatus);

	const label =
		status === "live"
			? t("realtime.live", "Live")
			: status === "connecting"
				? t("realtime.connecting", "Connecting…")
				: t("realtime.off", "Live updates off");

	return (
		<span
			className={cn(
				"inline-flex items-center gap-1.5 text-xs text-muted-foreground",
				className,
			)}
			// Announced politely rather than as an alert: this is ambient status,
			// and it changes on every reconnect.
			aria-live="polite"
			data-status={status}
		>
			<span
				aria-hidden="true"
				className={cn(
					"size-1.5 rounded-full",
					status === "live"
						? "bg-emerald-500"
						: status === "connecting"
							? "bg-yellow-500"
							: "bg-slate-400",
				)}
			/>
			{label}
		</span>
	);
}
